import { PaymentTimelineStatusComponent } from '../../../data-grid/utility/payment-timeline-status/payment-timeline-status.component';
import { NgClass, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, ChangeDetectorRef, input, inject, output, effect } from '@angular/core';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AuthSevices, ClientService, DepositTerminologyService, InvoiceService, TimeconversionService, ToastMessageService } from '@fitness-force/api';
import type { InvoiceOccurance, PaymentTimeline, SubscriptionById } from '@fitness-force/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { DepositTranslatePipe } from '@fitness-force/pipes/common-pipes';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { TimelineModule } from 'primeng/timeline';
import { BehaviorSubject } from 'rxjs';
import { DataGridComponent, EmptyStateContainerComponent } from '@fitness-force/grid';
import { DateFormatedComponent} from '@fitness-force/grid-utility'
import { InvoicePaymentAmountComponent } from '../../../data-grid/utility/invoice-payment-amount/invoice-payment-amount.component';
import { TooltipModule } from 'primeng/tooltip';
import { InvoiceDunningStatus, PaymentDerivedStatus, RecurringOccuranceType } from '@fitness-force/enum';
import { TagModule } from 'primeng/tag';
import { StyleClassModule } from 'primeng/styleclass';
import { AppErrors } from '@fitness-force/models';
import { PaymentTimelineActionsComponent } from '@fitness-force/grid-utility/action';
import { EditPaymentTimelinePopupComponent } from './edit-payment-timeline-popup/edit-payment-timeline-popup.component';

@Component({
  selector: 'payment-timeline',
  templateUrl: './payment-timeline.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DividerModule,
    TimelineModule,
    CurrencyformatPipe,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    DialogModule,
    DataGridComponent,
    TableModule,
    TooltipModule,
    StyleClassModule,
    TagModule,
    TranslateModule, DepositTranslatePipe, DateTimeFormatPipe, NgClass, AsyncPipe,
    EditPaymentTimelinePopupComponent],
})
export class PaymentTimelineComponent implements OnInit, OnDestroy {
  private static readonly REFRESH_DELAY_AFTER_EDIT_MS = 2000;

  translate = inject(TranslateService);
  private readonly depositTerminology = inject(DepositTerminologyService);
  private readonly timeService = inject(TimeconversionService);
  cd = inject(ChangeDetectorRef);
  private readonly invoiceService = inject(InvoiceService);
  private readonly clientService = inject(ClientService);
  private readonly toastService = inject(ToastMessageService);
  private readonly authService = inject(AuthSevices);


  readonly paymentTimelineData = input<InvoiceOccurance[]>([]);
  readonly subscription = input<SubscriptionById>();
  readonly clientId = input<number>(0);
  readonly subscriptionId = input<number>(0);
  readonly timelineUpdated = output<InvoiceOccurance[]>();
  readonly canShowEditOccurrence = input<boolean>(false);
  currentDate: Date = new Date();
  paymentTimelineModal: boolean = false;
  editPaymentTimelineModal = false;
  timelineData$ = new BehaviorSubject<InvoiceOccurance[]>([]);
  filtetredTimelineData$ = new BehaviorSubject<InvoiceOccurance[]>([]);
  timelineDataTablecolumns$ = new BehaviorSubject<any>([]);
  selecall: boolean = false;
  emptyStateComponentForHistory$ = new BehaviorSubject<any>(EmptyStateContainerComponent);
  selectedTimeline:InvoiceOccurance;
  isbuttonclicked:boolean=false;
  private refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;
  currentLggedInUser: any;


  constructor() {
    effect(() => {
      this.syncTimelineFromInput(this.paymentTimelineData());
    });
  }

  ngOnInit(): void {
    this.syncTimelineFromInput(this.paymentTimelineData());
    this.currentLggedInUser = this.authService.currentUserDetails();
  }

  ngOnDestroy(): void {
    this.clearScheduledRefresh();
  }

  private clearScheduledRefresh(): void {
    if (this.refreshTimeoutId !== null) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }

  private syncTimelineFromInput(timelineData: InvoiceOccurance[]): void {
    if (!timelineData?.length) {
      this.timelineData$.next([]);
      this.filtetredTimelineData$.next([]);
      return;
    }

    const timelineWithCancelledFlag = timelineData.map((occurrence) => ({
      ...occurrence,
      is_cancelled_after_subscription_end:
        this.isAfterSubscriptionEndDate(occurrence),
    }));

    const sortedPaymentTimelineData = [...timelineWithCancelledFlag].sort(
      (a: InvoiceOccurance, b: InvoiceOccurance) => {
        const dateDiff =
          this.toLocalOccurrenceDate(a.occurance_start_date).getTime() -
          this.toLocalOccurrenceDate(b.occurance_start_date).getTime();
        if (dateDiff !== 0) {
          return dateDiff;
        }

        return (a.occurance ?? 0) - (b.occurance ?? 0);
      },
    );

    this.timelineData$.next(sortedPaymentTimelineData);
    this.filtetredTimelineData$.next(this.filterOccurrencesByDate(sortedPaymentTimelineData));
    this.cd.detectChanges();
  }

  private resolveTimelineRequestIds(): { clientId: number; subscriptionId: number } | null {
    const clientId =
      this.clientId() ||
      this.selectedTimeline?.occurance_client_id ||
      this.paymentTimelineData()[0]?.occurance_client_id;
    const subscriptionId =
      this.subscriptionId() ||
      this.selectedTimeline?.occurance_subscription_id ||
      this.paymentTimelineData()[0]?.occurance_subscription_id;

    if (!clientId || !subscriptionId) {
      return null;
    }

    return { clientId, subscriptionId };
  }

  refreshPaymentTimelineData(
    delayMs = 0,
  ): void {
    const requestIds = this.resolveTimelineRequestIds();
    if (!requestIds) {
      return;
    }

    this.clearScheduledRefresh();

    const fetchTimeline = (): void => {
      this.clientService
        .getPaymentTimeline(requestIds.clientId, requestIds.subscriptionId)
        .subscribe({
          next: (response: PaymentTimeline) => {
            const timelineData = response?.invoice_occurance ?? [];
            this.syncTimelineFromInput(timelineData);
            this.timelineUpdated.emit(timelineData);
          },
          error: (error: AppErrors) => {
            this.toastService.showError(error);
            this.cd.detectChanges();
          },
        });
    };

    if (delayMs > 0) {
      this.refreshTimeoutId = setTimeout(() => {
        this.refreshTimeoutId = null;
        fetchTimeline();
      }, delayMs);
      return;
    }

    fetchTimeline();
  }

  isDateStartingAfterToday(startDate: Date) {
    startDate = new Date(startDate);
    // Compare dates properly by resetting time to midnight
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    const current = new Date(this.currentDate);
    current.setHours(0, 0, 0, 0);
    return start.getTime() > current.getTime();
  }

  ShowTimelineData() {
    this.TimelineColumnsTransalation();
    this.paymentTimelineModal = true;
    this.cd.detectChanges();
  }

  TimelineColumnsTransalation() {
    this.translate
      .get([
        'SUBSCRIPTIONS.PAYMENT_TIMELINE.DATE',
        'SUBSCRIPTIONS.PAYMENT_TIMELINE.AMOUNT',
        'SUBSCRIPTIONS.PAYMENT_TIMELINE.STATUS',
        'SUBSCRIPTIONS.PAYMENT_TIMELINE.FAILED_REASON'
      ])
      .subscribe((translations) => {
        this.GetTimelineColumns(translations);
      });
  }

  GetTimelineColumns(translations: any) {
    const columns = {
      Date: translations['SUBSCRIPTIONS.PAYMENT_TIMELINE.DATE'],
      Amount: translations['SUBSCRIPTIONS.PAYMENT_TIMELINE.AMOUNT'],
      Staus: translations['SUBSCRIPTIONS.PAYMENT_TIMELINE.STATUS'],
      Reason : translations['SUBSCRIPTIONS.PAYMENT_TIMELINE.FAILED_REASON']
    };
    this.timelineDataTablecolumns$.next([
      {
        title: columns.Date,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'occurance_start_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'occurance_start_date', format: 'defaultformat' },
        },
      },
      {
        title: columns.Amount,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'payment_amount_in_minor_currency',
        type: 'component',
        data: {
          name: InvoicePaymentAmountComponent,
          fetch_key: 'payment_amount_in_minor_currency',
        },
      },
      {
        title: columns.Staus,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'payment_status',
        type: 'component',
        data: {
          name: PaymentTimelineStatusComponent
        }
      },
      {
        title: columns.Reason,
        classes: 'py-3 text-600 w-15rem white-space-wrapper',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'failed_reason',
        type: 'default'
      },
      {
        title: "",
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        dataProperty: 'actions',
        type: 'component',
        data: {
          name: PaymentTimelineActionsComponent,
          additionData: true,
          aData: [
            'retrypayment',
            ...(this.canShowEditOccurrence() ? ['editoccurrence'] : []),
          ],
        },
      }
    ]);
  }

  onActionEvent(event: any) {
    switch (event.action) {
      case 'retrypayment':
        {
          this.selectedTimeline = event.columnData;
          this.retryPayment();
        }
        break;
      case 'editoccurrence':
        {
          this.selectedTimeline = event.columnData;
          this.editPaymentTimelineModal = true;
          this.cd.detectChanges();
        }
        break;
    }
  }

  closeEditPaymentTimelinePopup(): void {
    this.editPaymentTimelineModal = false;
    this.cd.detectChanges();
  }

  onEditPaymentTimelineSaved(): void {
    this.refreshPaymentTimelineData(PaymentTimelineComponent.REFRESH_DELAY_AFTER_EDIT_MS);
  }

  callRetryPayment(timeline: InvoiceOccurance) {
    this.selectedTimeline = timeline;
    this.retryPayment();
  }

  retryPayment() {
    this.isbuttonclicked=true;
    this.invoiceService.RetryFailedPayment(this.selectedTimeline.occurance_tenant_id, this.selectedTimeline.occurance_client_id, this.selectedTimeline.occurance_invoice_id).subscribe({
      next: () => {
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.RETRY_MSG')
          .subscribe((translation) => {
            this.toastService.showSuccess(translation);
          });
          this.isbuttonclicked=false;
          this.cd.detectChanges();  
      },
      error: (error: AppErrors) => {
          this.isbuttonclicked=false;
          this.toastService.showError(error);
          this.cd.detectChanges();  
      },
    });
  }

  get PaymentTimeline()
  {
    return new BehaviorSubject(this.paymentTimelineData());
  }

  public get paymentStatus()
  {
    return PaymentDerivedStatus;
  }

  public get recurringOccuranceType()
  {
    return RecurringOccuranceType;
  }

  get canDoMidCycleDiscount(): boolean {
    return this.currentLggedInUser && this.canShowEditOccurrence() && (this.currentLggedInUser.role.toLowerCase() == 'admin' || this.currentLggedInUser.can_admin_billing == 'true')
  }

  // Function to filter occurrences within the previous 3 months and next 5 months
  filterOccurrencesByDate(occurrences: InvoiceOccurance[]): InvoiceOccurance[] {
    if (!occurrences || occurrences.length === 0) {
      return [];
    }

    const currentDate = new Date();
    
    // Find the first occurrence date to determine if subscription is future-dated
    const firstOccurrenceDate = this.toLocalOccurrenceDate(
      occurrences[0].occurance_start_date,
    );
    const isFutureSubscription = firstOccurrenceDate > currentDate;

    let startFilterDate: Date;
    let endFilterDate: Date;

    if (isFutureSubscription) {
      // For future subscriptions: start from the first occurrence date
      startFilterDate = new Date(firstOccurrenceDate);
      // Show next 8 months from the first occurrence
      endFilterDate = new Date(firstOccurrenceDate);
      endFilterDate.setMonth(firstOccurrenceDate.getMonth() + 8);
    } else {
      // For ongoing subscriptions: show last 3 months from today
      startFilterDate = new Date(currentDate);
      startFilterDate.setMonth(currentDate.getMonth() - 3);
      // Show next 5 months from today
      endFilterDate = new Date(currentDate);
      endFilterDate.setMonth(currentDate.getMonth() + 5);
    }

    // Filter occurrences based on the date range
    const filteredOccurrences = occurrences.filter(occurrence => {
      const startDate = this.toLocalOccurrenceDate(occurrence.occurance_start_date);
      return startDate >= startFilterDate && startDate <= endFilterDate;
    });

    return filteredOccurrences;
  }

  getTooltipMessage(OcurenctType:InvoiceOccurance)
  {
    let message:string="";
    const data = this.resolveOccurrenceType(OcurenctType.occurance_type);
    switch (data) {
      case RecurringOccuranceType.Deposit:
        message = this.depositTerminology.instant('DEPOSIT.ADDED_ON_OCCURRENCE');
        break;
      case RecurringOccuranceType.EndProrata:
        this.translate.get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ERROR_MSG')
        .subscribe((translations: any) => {
          message = translations;
        });
        break;
      case RecurringOccuranceType.Freeze:
        this.translate.get('SUBSCRIPTIONS.PAYMENT_TIMELINE.FREEZE_CYCLE_MSG')
        .subscribe((translations: any) => {
          message = translations;
        });
        break;
      case RecurringOccuranceType.InitialProRata:
        this.translate.get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ERROR_MSG')
        .subscribe((translations: any) => {
          message = translations;
        });
        
        break;
      case RecurringOccuranceType.MiddCycleAddon:
        this.translate.get('SUBSCRIPTIONS.PAYMENT_TIMELINE.MID_CYCLE_ADDON_MSG')
        .subscribe((translations: any) => {
          message = translations;
        });
        break;
      case RecurringOccuranceType.MiddCycleDiscount:
        this.translate.get('SUBSCRIPTIONS.PAYMENT_TIMELINE.MID_CYCLE_DIS_MSG')
        .subscribe((translations: any) => {
          message = translations;
        });
        break;
      case RecurringOccuranceType.Recurring:
        this.translate.get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ERROR_MSG')
        .subscribe((translations: any) => {
          message = translations;
        });
        break;
      case RecurringOccuranceType.Enhancement:
        this.translate.get('SUBSCRIPTIONS.PAYMENT_TIMELINE.ENHANCEMENT_MSG')
        .subscribe((translations: string) => {
          message = translations;
        });
        break;
      case RecurringOccuranceType.BillingStopped:
        this.translate
          .get('SUBSCRIPTIONS.PAYMENT_TIMELINE.BILLING_STOPPED_MSG')
          .subscribe((translations: string) => {
            message = translations;
          });
        break;
      }
    return message;
  }

  isSuccessfulPayment(timeline: InvoiceOccurance): boolean {
    return timeline.payment_status === this.paymentStatus.Successfull ||
      timeline.payment_status?.toString() === this.paymentStatus[this.paymentStatus.Successfull];
  }

  isfailedPayment(timeline: InvoiceOccurance): boolean {
    return timeline.payment_status === this.paymentStatus.Failed ||
      timeline.payment_status?.toString() === this.paymentStatus[this.paymentStatus.Failed];
  }

  isOccurenceDiscount(timeline: InvoiceOccurance): boolean {
    return timeline.occurance_type === this.recurringOccuranceType.MiddCycleDiscount ||
      timeline.occurance_type?.toString() === this.recurringOccuranceType[this.recurringOccuranceType.MiddCycleDiscount];
  }

  isInitialFailedPayment(timeline: InvoiceOccurance): boolean {
    return (timeline.occurance_type != RecurringOccuranceType.Recurring || timeline.occurance_type.toString() != RecurringOccuranceType[RecurringOccuranceType.Recurring])
    && (timeline.occurance_type != RecurringOccuranceType.InitialFullRecurring || timeline.occurance_type.toString() != RecurringOccuranceType[RecurringOccuranceType.InitialFullRecurring])
    && (timeline.payment_status !== PaymentDerivedStatus.Failed || timeline.payment_status.toString() !== PaymentDerivedStatus[PaymentDerivedStatus.Failed])
  }

  isFailedPaymentWithRecurring(timeline: InvoiceOccurance): boolean {
    return (timeline.payment_status === this.paymentStatus.Failed ||
      timeline.payment_status?.toString() === this.paymentStatus[this.paymentStatus.Failed]) && timeline.occurance !== 1 && (timeline.occurance_dunning_status === InvoiceDunningStatus.Exhausted || timeline.occurance_dunning_status?.toString() === InvoiceDunningStatus[InvoiceDunningStatus.Exhausted]);
  }

  isCreatedPayment(timeline: InvoiceOccurance): boolean {
    return ((timeline.payment_status === this.paymentStatus.Created || timeline.payment_status?.toString() === this.paymentStatus[this.paymentStatus.Created]) 
    || (timeline.payment_status == PaymentDerivedStatus.Initiated || timeline.payment_status?.toString() == PaymentDerivedStatus[PaymentDerivedStatus.Initiated]));
  }

  isCreatedPaymentWithInvoice(timeline: InvoiceOccurance): boolean {
    return timeline.occurance_invoice_id > 0 && timeline.payment_amount_in_minor_currency == 0 && timeline.occurance_payment_id == 0
    && ((timeline.payment_status == PaymentDerivedStatus.Created || timeline.payment_status?.toString() == PaymentDerivedStatus[PaymentDerivedStatus.Created])
    || (timeline.payment_status == PaymentDerivedStatus.Initiated || timeline.payment_status?.toString() == PaymentDerivedStatus[PaymentDerivedStatus.Initiated]))
    && (timeline.occurance_type == RecurringOccuranceType.Recurring || timeline.occurance_type?.toString() == RecurringOccuranceType[RecurringOccuranceType.Recurring]);
  }

  shouldShowIcon(timeline: InvoiceOccurance): boolean {
    let result:boolean=false;
    const data = this.resolveOccurrenceType(timeline.occurance_type);
    switch (data) {
      case RecurringOccuranceType.Deposit:
       result= true;
        break;
      case RecurringOccuranceType.EndProrata:
        result= false;

        break;
      case RecurringOccuranceType.Freeze:
        result= true;
        break;
      case RecurringOccuranceType.InitialProRata:
        result= false;
        break;
      case RecurringOccuranceType.MiddCycleAddon:
        result= true;
        break;
      case RecurringOccuranceType.MiddCycleDiscount:
        result= true;
        break;
      case RecurringOccuranceType.Recurring:
        result= false;
        break;
      case RecurringOccuranceType.Enhancement:
        result= true;
        break;
      case RecurringOccuranceType.BillingStopped:
        result= true;
        break;
      }
    return result;
  }

  isEnhancementOccurance(timeline: InvoiceOccurance): boolean {
    return (
      this.resolveOccurrenceType(timeline.occurance_type) ===
      RecurringOccuranceType.Enhancement
    );
  }

  shouldDisplayEnhancementTag(item: InvoiceOccurance): boolean {
    return this.isEnhancementOccurance(item) && this.isStartOfSequence(item);
  }

  getTimelineMarkerClass(timeline: InvoiceOccurance): string {
    if (this.isEnhancementOccurance(timeline)) {
      return 'custom-marker h-2rem w-full max-w-2rem border-circle p-button p-0 bg-indigo-500 flex align-items-center justify-content-center';
    }

    if (
      timeline.payment_status &&
      this.isSuccessfulPayment(timeline)
    ) {
      return 'custom-marker h-2rem w-full max-w-2rem border-circle p-button p-0 bg-green-500 flex align-items-center justify-content-center';
    }

    if (timeline.payment_status && this.isCreatedPayment(timeline)) {
      return 'custom-marker h-2rem w-full max-w-2rem border-circle p-button p-0 bg-yellow-500 flex align-items-center justify-content-center';
    }

    return 'custom-marker h-2rem w-full max-w-2rem border-circle border-2 border-400 flex align-items-center justify-content-center';
  }

  private resolveOccurrenceType(
    occuranceType: RecurringOccuranceType | string | number
  ): RecurringOccuranceType | undefined {
    if (typeof occuranceType === 'number') {
      return occuranceType as RecurringOccuranceType;
    }

    return RecurringOccuranceType[
      occuranceType.toString() as keyof typeof RecurringOccuranceType
    ];
  }

  isBillingStoppedOccurance(timeline: InvoiceOccurance): boolean {
    return (
      timeline.occurance_type === RecurringOccuranceType.BillingStopped ||
      timeline.occurance_type?.toString() ===
        RecurringOccuranceType[RecurringOccuranceType.BillingStopped]
    );
  }

  isFreezeOccurance(timeline: InvoiceOccurance): boolean {
    return (
      timeline.occurance_type === RecurringOccuranceType.Freeze ||
      timeline.occurance_type?.toString() ===
        RecurringOccuranceType[RecurringOccuranceType.Freeze]
    );
  }

  shouldDisplayDepositTag(item: InvoiceOccurance): boolean {
    // Check if the current item is the start of a sequence
    return this.isStartOfSequence(item);
  }

  private isStartOfSequence(item: InvoiceOccurance): boolean {
    const currentIndex = this.filtetredTimelineData$.value.indexOf(item);

    // Check if the current item is the first in the array
    if (currentIndex === 0) {
      return true;
    }
    // Check if the current item has a different isDeposit value than the previous item
    const previousItem = this.filtetredTimelineData$.value[currentIndex - 1];
    return item.occurance_type !== previousItem.occurance_type;
  }

  isAfterSubscriptionEndDate(timeline: InvoiceOccurance): boolean {
    const subscriptionEndDate = this.getSubscriptionEndDate();
    if (!subscriptionEndDate) {
      return false;
    }

    const occurrenceStart = this.toLocalOccurrenceDate(timeline.occurance_start_date);
    occurrenceStart.setHours(0, 0, 0, 0);

    const endDate = new Date(subscriptionEndDate);
    endDate.setHours(0, 0, 0, 0);

    return occurrenceStart.getTime() > endDate.getTime();
  }

  private getSubscriptionEndDate(): Date | null {
    const sub = this.subscription();
    if (!sub) {
      return null;
    }

    const rawEndDate = sub.scheduled_cancellation_date ?? sub.end_date;
    if (!rawEndDate) {
      return null;
    }

    const endDate = new Date(rawEndDate);
    if (this.isMinimumDateTimeValue(endDate)) {
      return null;
    }

    return endDate;
  }

  private isMinimumDateTimeValue(date: Date): boolean {
    return new Date(date).getTime() === new Date('0001-01-01T00:00:00').getTime();
  }

  private toLocalOccurrenceDate(date: Date | string): Date {
    return this.timeService.ConvertUTCToLocalDateTime(date);
  }

}
